// A stored PDF, rendered by pdf.js into canvases with its text layer above.
//
// This replaces the browser's own viewer in a frame, which was the open item
// in SourceViewer's sandbox note: pdf.js renders the pages itself, so the page
// is ours to mark, and the frame's `allow-same-origin` is gone with it.
//
// What pdf.js is given, and what it is not:
//
//   * The bytes the API served, as a copy. No URL — pdf.js never fetches.
//   * `enableXfa: false`: XFA forms are an HTML-like layer we do not render.
//   * No annotation layer: links and form widgets are not drawn, so nothing on
//     the page is clickable. A reviewer reads the source; they do not use it.
//   * No scripting: pdfjs-dist runs document JavaScript only through its
//     separate sandbox bundle, which this app does not load. Intake already
//     refuses a PDF carrying /JavaScript, /Launch or /OpenAction.
//
// Highlighting follows SourceViewer's rules and adds one: the excerpt must be
// found on the rendered page exactly once (see evidence.ts). A field whose
// excerpt is missing or appears twice is reported back as such and not marked.
// Highlights are whole text-layer items — the smallest unit pdf.js lays out —
// so a mark can be slightly wider than the evidence, never somewhere else.
//
// Performance, as measured on 2026-10-21 (scripts/visual/measure-docs.ts):
// the page is DRAWN once per document and column width, and never because the
// document was polled or its fields changed. Marking, focus and a replay's
// lighting are classes on the already-drawn text layer. pdf.js and its worker
// are preloaded when the Documents screen opens (preloadPdfJs), and one worker
// is reused for every document rather than spawned per page view.
//
// Cropping: a page is drawn zoomed to its CONTENT — the box around everything
// printed on it, found on a quick low-resolution pass, plus a margin — so a
// small invoice on a full A4 sheet is readable in a half-width column. Only
// that region is drawn. Nothing is cut that has ink on it; the full page,
// margins and all, is one click away (`crop={false}` in the full-size view).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ExtractedField } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { locateEvidence, type EvidenceLocation } from './evidence.ts';

/** Pages beyond this are not rendered; the legend says how many there are. */
export const PDF_PAGE_LIMIT = 20;

/** Margin kept around the content when cropping, in PDF points. */
const CROP_MARGIN = 14;
/** The most a cropped page is zoomed, so a one-line page is not drawn as a poster. */
const MAX_ZOOM = 3;
/** A pixel darker than this, on any channel, is ink. */
const INK = 242;

type PdfJs = typeof import('pdfjs-dist');
interface Loaded {
  readonly pdfjs: PdfJs;
  /**
   * One worker for every document, passed to each getDocument explicitly. A
   * worker pdf.js did not create is one it does not destroy with a loading
   * task — and a task IS destroyed whenever a view is redrawn mid-load (the
   * column width settling), which with a shared port took the worker down
   * and failed the next load ("PDFWorker.create - the worker is being
   * destroyed"). Undefined where there is no Worker (tests).
   */
  readonly worker: InstanceType<PdfJs['PDFWorker']> | undefined;
}
let loading: Promise<Loaded> | null = null;

/**
 * pdf.js and one long-lived worker — loaded once. Called when the Documents
 * screen opens, so the first document does not wait for a chunk and a worker
 * to download after it was clicked.
 */
export function preloadPdfJs(): Promise<Loaded> {
  loading ??= (async () => {
    const [pdfjs, workerUrl] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl.default;
    const worker =
      typeof Worker === 'undefined'
        ? undefined
        : // pdfjs-dist's .d.ts types `port` as null|undefined; its documentation
          // and runtime take a Worker, which is what this passes.
          new pdfjs.PDFWorker({ port: new Worker(workerUrl.default, { type: 'module' }) } as unknown as ConstructorParameters<PdfJs['PDFWorker']>[0]);
    return { pdfjs, worker };
  })();
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

export interface PdfViewProps {
  readonly bytes: Uint8Array;
  /** Fields to highlight: same version, with a span and an excerpt. */
  readonly fields: readonly ExtractedField[];
  /**
   * Fields with recorded OCR boxes (0034) — a scanned PDF has no text layer to
   * search, so these are drawn where Tesseract read them.
   */
  readonly boxed?: readonly ExtractedField[];
  /** During a replay, the fields lit so far; null when every placed field is lit. */
  readonly lit?: ReadonlySet<string> | null;
  readonly focused: string | null;
  /** Where each field's evidence was (or was not) found, once the pages are read. */
  readonly onLocated: (located: ReadonlyMap<string, EvidenceLocation>) => void;
  /** Draw each page zoomed to its content (the column view). False: the whole page. */
  readonly crop?: boolean;
}

type Status = { kind: 'loading' } | { kind: 'ready'; pages: number } | { kind: 'failed' };

interface DrawnPage {
  readonly number: number;
  readonly wrapper: HTMLElement;
  readonly divs: HTMLElement[];
  readonly items: string[];
  /** Page points → CSS pixels of the drawn region, for the OCR boxes. */
  readonly place: (box: { x: number; y: number; w: number; h: number }) => { left: number; top: number; width: number; height: number };
}

export function PdfView({ bytes, fields, boxed = [], lit = null, focused, onLocated, crop = true }: PdfViewProps): ReactNode {
  const t = useT();
  const container = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const drawn = useRef<DrawnPage[]>([]);
  /** The OCR boxes drawn over scanned pages. */
  const boxMarks = useRef<HTMLElement[]>([]);
  const onLocatedRef = useRef(onLocated);
  onLocatedRef.current = onLocated;

  // The pages are drawn to the container's width, so they are drawn again when
  // that width settles or changes — once the grid around them has laid out, and
  // when the window is resized. Rounded to 16px so a scrollbar appearing does
  // not redraw them.
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const host = container.current;
    if (host === null || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      setWidth((current) => (Math.abs(current - measured) >= 16 ? Math.floor(measured) : current));
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  // 1. DRAW — once per document, width and crop mode. Nothing about the fields.
  useEffect(() => {
    let cancelled = false;
    let destroy: (() => void) | undefined;
    const host = container.current;
    if (host === null) return undefined;
    setStatus({ kind: 'loading' });

    void (async () => {
      try {
        const { pdfjs, worker } = await preloadPdfJs();
        const task = pdfjs.getDocument({ data: bytes.slice(), enableXfa: false, ...(worker === undefined ? {} : { worker }) });
        destroy = () => void task.destroy();
        const document = await task.promise;
        if (cancelled) return;

        const target = width > 0 ? width : host.clientWidth > 0 ? host.clientWidth : 640;
        const count = Math.min(document.numPages, PDF_PAGE_LIMIT);
        const pages: DrawnPage[] = [];
        const fragment = window.document.createDocumentFragment();
        for (let number = 1; number <= count; number += 1) {
          const page = await document.getPage(number);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const region = crop ? await contentBox(page, base.width, base.height) : null;
          if (cancelled) return;
          const area = region ?? { x: 0, y: 0, w: base.width, h: base.height };
          const scale = Math.min(target / area.w, crop ? MAX_ZOOM : Number.POSITIVE_INFINITY);
          const viewport = page.getViewport({ scale, offsetX: -area.x * scale, offsetY: -area.y * scale });
          const cssWidth = Math.ceil(area.w * scale);
          const cssHeight = Math.ceil(area.h * scale);

          const wrapper = window.document.createElement('div');
          wrapper.className = `pdf-page${region === null ? '' : ' cropped'}`;
          wrapper.style.setProperty('--scale-factor', String(scale));
          wrapper.style.width = `${cssWidth}px`;
          wrapper.style.height = `${cssHeight}px`;
          wrapper.setAttribute('data-page', String(number));
          const canvas = window.document.createElement('canvas');
          const ratio = window.devicePixelRatio || 1;
          canvas.width = Math.floor(cssWidth * ratio);
          canvas.height = Math.floor(cssHeight * ratio);
          canvas.style.width = `${cssWidth}px`;
          canvas.style.height = `${cssHeight}px`;
          const layer = window.document.createElement('div');
          layer.className = 'textLayer';
          wrapper.append(canvas, layer);

          await page.render({
            canvas,
            viewport,
            ...(ratio === 1 ? {} : { transform: [ratio, 0, 0, ratio, 0, 0] }),
          }).promise;
          // The text layer lays out the WHOLE page at this scale and ignores a
          // viewport's offsets, so it gets the unshifted viewport and is moved
          // by the crop itself; the wrapper clips it to the drawn region.
          layer.style.inset = 'auto';
          layer.style.left = `${-area.x * scale}px`;
          layer.style.top = `${-area.y * scale}px`;
          const text = new pdfjs.TextLayer({
            textContentSource: await page.getTextContent(),
            container: layer,
            viewport: page.getViewport({ scale }),
          });
          await text.render();
          if (cancelled) return;
          pages.push({
            number,
            wrapper,
            divs: text.textDivs,
            items: text.textContentItemsStr,
            // OCR boxes are fractions of the whole page.
            place: (box) => ({
              left: (box.x * base.width - area.x) * scale,
              top: (box.y * base.height - area.y) * scale,
              width: box.w * base.width * scale,
              height: box.h * base.height * scale,
            }),
          });
          fragment.append(wrapper);
        }
        // Swapped in whole, so the old pages stay on screen until the new ones are ready.
        host.replaceChildren(fragment);
        drawn.current = pages;
        boxMarks.current = [];
        setStatus({ kind: 'ready', pages: document.numPages });
      } catch {
        if (!cancelled) setStatus({ kind: 'failed' });
      }
    })();

    return () => {
      cancelled = true;
      destroy?.();
    };
  }, [bytes, width, crop]);

  // 2. MARK — whenever the fields change (a reading landing, a new revision),
  // on the pages already drawn.
  const fieldKey = [
    ...fields.map((field) => `${field.fieldPath}\u0000${field.page ?? ''}\u0000${field.evidenceText ?? ''}`),
    ...boxed.map((field) => `${field.fieldPath}\u0000${JSON.stringify(field.evidenceBoxes ?? [])}`),
  ].join('\u0001');
  useEffect(() => {
    if (status.kind !== 'ready') return;
    const pages = drawn.current;
    for (const page of pages) {
      for (const div of page.divs) {
        div.classList.remove('source-value');
        delete div.dataset['fields'];
        div.removeAttribute('title');
      }
    }
    for (const mark of boxMarks.current) mark.remove();
    boxMarks.current = [];

    const found = new Map<string, EvidenceLocation>();
    for (const field of fields) {
      if (field.evidenceText === null) continue;
      const location = locateEvidence(pages.map((page) => page.items), field.evidenceText, field.page);
      found.set(field.fieldPath, location);
      if (location.kind !== 'located') continue;
      const divs = pages[location.page - 1]?.divs ?? [];
      for (const index of location.items) {
        const div = divs[index];
        if (div === undefined) continue;
        div.classList.add('source-value');
        const marked = new Set((div.dataset['fields'] ?? '').split(' ').filter(Boolean));
        marked.add(field.fieldPath);
        div.dataset['fields'] = [...marked].join(' ');
        div.title = [...marked].join(', ');
      }
    }
    for (const field of boxed) {
      for (const box of field.evidenceBoxes ?? []) {
        const page = pages.find((entry) => entry.number === box.page);
        if (page === undefined) continue;
        const at = page.place(box);
        const mark = window.document.createElement('span');
        mark.className = 'source-value ocr-box';
        mark.dataset['fields'] = field.fieldPath;
        mark.title = field.fieldPath;
        Object.assign(mark.style, {
          left: `${at.left}px`,
          top: `${at.top}px`,
          width: `${at.width}px`,
          height: `${at.height}px`,
        });
        page.wrapper.append(mark);
        boxMarks.current.push(mark);
      }
    }
    onLocatedRef.current(found);
    // fieldKey stands for `fields` and `boxed`.
  }, [status, fieldKey]);

  // 3. LIGHT — focus, and what a replay has lit so far: classes only.
  const litKey = lit === null ? null : [...lit].join(' ');
  useEffect(() => {
    const litNow = litKey === null ? null : new Set(litKey.split(' '));
    for (const div of [...drawn.current.flatMap((page) => page.divs), ...boxMarks.current]) {
      const fieldsHere = (div.dataset['fields'] ?? '').split(' ').filter(Boolean);
      div.classList.toggle('focused', focused !== null && fieldsHere.includes(focused));
      div.classList.toggle('lit', fieldsHere.length > 0 && (litNow === null || fieldsHere.some((field) => litNow.has(field))));
    }
  }, [focused, status, litKey, fieldKey]);

  return (
    <div className="invoice pdf-view">
      <div ref={container} data-testid="pdf-pages" />
      {status.kind === 'loading' ? (
        <div className="pdf-skeleton" role="status" aria-label={t('source.pdf.rendering')}>
          <span />
          <span />
          <span />
          <span />
        </div>
      ) : null}
      {status.kind === 'failed' ? (
        <p className="inline-hint" role="status">
          {t('source.pdf.failed')}
        </p>
      ) : null}
      {status.kind === 'ready' && status.pages > PDF_PAGE_LIMIT ? (
        <p className="muted small">
          {t('source.pdf.pageLimit', { shown: PDF_PAGE_LIMIT, pages: status.pages })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The box around everything printed on the page, in PDF points, with a margin;
 * null when the page is blank or nothing would be gained. Found on a quick
 * pass at half a pixel per point, which is enough to see where the ink is.
 */
async function contentBox(
  page: Awaited<ReturnType<Awaited<ReturnType<PdfJs['getDocument']>['promise']>['getPage']>>,
  pageWidth: number,
  pageHeight: number,
): Promise<{ x: number; y: number; w: number; h: number } | null> {
  const scale = 0.5;
  const viewport = page.getViewport({ scale });
  const canvas = window.document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) return null;
  await page.render({ canvas, viewport }).promise;
  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      if ((data[i] ?? 255) < INK || (data[i + 1] ?? 255) < INK || (data[i + 2] ?? 255) < INK) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0) return null;
  const x = Math.max(0, left / scale - CROP_MARGIN);
  const y = Math.max(0, top / scale - CROP_MARGIN);
  const w = Math.min(pageWidth, (right + 1) / scale + CROP_MARGIN) - x;
  const h = Math.min(pageHeight, (bottom + 1) / scale + CROP_MARGIN) - y;
  // Not worth a crop when the content already fills the page.
  if (w > pageWidth * 0.92 && h > pageHeight * 0.92) return null;
  return { x, y, w, h };
}
